<template>
  <div
    ref="rootEl"
    class="group relative h-full w-full overflow-hidden bg-black outline-none"
    tabindex="0"
    role="application"
    aria-label="Webinar replay video player"
    @mousemove="showPlayerControls"
    @mouseleave="scheduleControlsHide"
    @keydown="handleKeydown"
    @dblclick="toggleFullscreen"
  >
    <video
      ref="videoEl"
      :src="src"
      :poster="poster || undefined"
      class="absolute inset-0 h-full w-full bg-black object-contain"
      playsinline
      preload="metadata"
      @click="togglePlay"
      @loadedmetadata="syncDuration"
      @loadeddata="isLoading = false"
      @timeupdate="syncProgress"
      @play="handlePlay"
      @pause="handlePause"
      @waiting="isLoading = true"
      @playing="isLoading = false"
      @ended="handleEnded"
      @error="handleError"
      @contextmenu.prevent
    />

    <div v-if="isLoading" class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/35">
      <div class="h-11 w-11 animate-spin rounded-full border-[3px] border-white/25 border-t-[#2d8cff]"></div>
    </div>

    <button
      v-if="isPaused && !isLoading"
      type="button"
      class="absolute left-1/2 top-1/2 z-10 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/60 text-white shadow-2xl backdrop-blur-sm transition hover:scale-105 hover:bg-[#2478ff]"
      aria-label="Play Webinar replay"
      @click.stop="togglePlay"
    >
      <PlayIcon class="ml-1 h-10 w-10" />
    </button>

    <Transition name="webinar-controls-fade">
      <div
        v-show="controlsVisible || isPaused"
        class="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/95 via-black/60 to-transparent px-4 pb-4 pt-14 sm:px-5"
        @mouseenter="controlsHovered = true"
        @mouseleave="handleControlsLeave"
        @click.stop
      >
        <input
          type="range"
          min="0"
          :max="Math.max(duration, 0.01)"
          step="0.05"
          :value="currentTime"
          class="webinar-progress-slider block w-full cursor-pointer"
          :style="progressStyle"
          aria-label="Replay position"
          @input="seek"
        />

        <div class="mt-3 flex items-center justify-between gap-3 text-white">
          <div class="flex min-w-0 items-center gap-2 sm:gap-3">
            <button type="button" class="webinar-control-button" :aria-label="isPaused ? 'Play' : 'Pause'" @click="togglePlay">
              <PlayIcon v-if="isPaused" class="ml-0.5 h-5 w-5" />
              <PauseIcon v-else class="h-5 w-5" />
            </button>

            <div class="group/volume flex items-center gap-2 rounded-full bg-black/50 px-2 py-1.5 backdrop-blur-sm sm:px-3">
              <button type="button" class="text-white/90 transition hover:text-[#65a7ff]" :aria-label="isMuted ? 'Unmute' : 'Mute'" @click="toggleMute">
                <SpeakerXMarkIcon v-if="isMuted || volume === 0" class="h-5 w-5" />
                <SpeakerWaveIcon v-else class="h-5 w-5" />
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                :value="volume"
                class="webinar-volume-slider hidden w-20 cursor-pointer sm:block"
                :style="volumeStyle"
                aria-label="Volume"
                @input="changeVolume"
              />
            </div>

            <span class="whitespace-nowrap rounded-full bg-black/50 px-3 py-2 text-xs font-semibold tabular-nums text-white/90 backdrop-blur-sm sm:text-sm">
              {{ formattedCurrentTime }} / {{ formattedDuration }}
            </span>
          </div>

          <div class="flex shrink-0 items-center gap-2">
            <span class="hidden rounded-full border border-white/10 bg-black/50 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-200 sm:inline-flex">
              HD replay
            </span>
            <button type="button" class="webinar-control-button" :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'" @click="toggleFullscreen">
              <ArrowsPointingInIcon v-if="isFullscreen" class="h-5 w-5" />
              <ArrowsPointingOutIcon v-else class="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <div v-if="playbackError" class="absolute inset-x-4 top-4 z-30 rounded-xl border border-red-400/25 bg-red-950/90 px-4 py-3 text-center text-sm text-red-100 backdrop-blur">
      {{ playbackError }}
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  PauseIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/vue/24/solid';

const props = defineProps({
  src: { type: String, required: true },
  poster: { type: String, default: '' },
});

const rootEl = ref(null);
const videoEl = ref(null);
const isPaused = ref(true);
const isLoading = ref(true);
const isMuted = ref(false);
const isFullscreen = ref(false);
const controlsVisible = ref(true);
const controlsHovered = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const volume = ref(1);
const lastVolume = ref(1);
const playbackError = ref('');
let hideTimer = null;

const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));
const progressStyle = computed(() => ({ '--progress-percent': `${duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0}%` }));
const volumeStyle = computed(() => ({ '--volume-percent': `${volume.value * 100}%` }));

function formatTime(value) {
  const seconds = Math.max(0, Math.floor(Number(value) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remaining = seconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`
    : `${minutes}:${String(remaining).padStart(2, '0')}`;
}

const formattedCurrentTime = computed(() => formatTime(currentTime.value));
const formattedDuration = computed(() => formatTime(duration.value));

async function togglePlay() {
  const video = videoEl.value;
  if (!video) return;
  playbackError.value = '';
  if (video.paused) {
    await video.play().catch(() => { playbackError.value = 'Playback could not start. Please try again.'; });
  } else {
    video.pause();
  }
  showPlayerControls();
}

function handlePlay() { isPaused.value = false; scheduleControlsHide(); }
function handlePause() { isPaused.value = true; controlsVisible.value = true; }
function handleEnded() { isPaused.value = true; controlsVisible.value = true; }
function handleError() { isLoading.value = false; playbackError.value = 'The Webinar replay could not be loaded.'; }
function syncDuration() { duration.value = Number(videoEl.value?.duration || 0); isLoading.value = false; }
function syncProgress() { currentTime.value = Number(videoEl.value?.currentTime || 0); }

function seek(event) {
  const next = clamp(event.target.value, 0, duration.value);
  if (videoEl.value) videoEl.value.currentTime = next;
  currentTime.value = next;
  showPlayerControls();
}

function changeVolume(event) {
  const next = clamp(event.target.value, 0, 1);
  volume.value = next;
  if (next > 0) lastVolume.value = next;
  isMuted.value = next === 0;
  if (videoEl.value) { videoEl.value.volume = next; videoEl.value.muted = next === 0; }
}

function toggleMute() {
  const video = videoEl.value;
  if (!video) return;
  if (video.muted || volume.value === 0) {
    const restore = lastVolume.value || 0.75;
    volume.value = restore; video.volume = restore; video.muted = false; isMuted.value = false;
  } else {
    lastVolume.value = volume.value || 1; video.muted = true; isMuted.value = true;
  }
}

function showPlayerControls() {
  controlsVisible.value = true;
  window.clearTimeout(hideTimer);
  if (!isPaused.value) scheduleControlsHide();
}

function scheduleControlsHide() {
  window.clearTimeout(hideTimer);
  if (isPaused.value || controlsHovered.value) return;
  hideTimer = window.setTimeout(() => { if (!controlsHovered.value && !isPaused.value) controlsVisible.value = false; }, 2200);
}

function handleControlsLeave() { controlsHovered.value = false; scheduleControlsHide(); }

function syncFullscreen() {
  const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
  isFullscreen.value = Boolean(rootEl.value && fullscreenElement && (fullscreenElement === rootEl.value || rootEl.value.contains(fullscreenElement)));
}

async function toggleFullscreen() {
  const root = rootEl.value;
  if (!root) return;
  if (isFullscreen.value) {
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    if (exit) await exit.call(document).catch(() => {});
  } else {
    const request = root.requestFullscreen || root.webkitRequestFullscreen;
    if (request) await request.call(root).catch(() => {});
  }
  syncFullscreen();
}

function handleKeydown(event) {
  if (event.target !== rootEl.value && event.target !== videoEl.value) return;
  if ([' ', 'k', 'K'].includes(event.key)) { event.preventDefault(); void togglePlay(); }
  else if (event.key === 'm' || event.key === 'M') toggleMute();
  else if (event.key === 'f' || event.key === 'F') void toggleFullscreen();
  else if (event.key === 'ArrowLeft' && videoEl.value) { event.preventDefault(); videoEl.value.currentTime = clamp(videoEl.value.currentTime - 5, 0, duration.value); }
  else if (event.key === 'ArrowRight' && videoEl.value) { event.preventDefault(); videoEl.value.currentTime = clamp(videoEl.value.currentTime + 5, 0, duration.value); }
}

watch(() => props.src, () => {
  isPaused.value = true; isLoading.value = true; currentTime.value = 0; duration.value = 0; playbackError.value = '';
  if (videoEl.value) videoEl.value.load();
});

onMounted(() => {
  document.addEventListener('fullscreenchange', syncFullscreen);
  document.addEventListener('webkitfullscreenchange', syncFullscreen);
});

onBeforeUnmount(() => {
  window.clearTimeout(hideTimer);
  document.removeEventListener('fullscreenchange', syncFullscreen);
  document.removeEventListener('webkitfullscreenchange', syncFullscreen);
});
</script>

<style scoped>
.webinar-control-button {
  display: inline-flex;
  width: 2.5rem;
  height: 2.5rem;
  align-items: center;
  justify-content: center;
  border: 1px solid rgb(255 255 255 / 12%);
  border-radius: 9999px;
  background: rgb(0 0 0 / 48%);
  color: white;
  backdrop-filter: blur(8px);
  transition: background-color 160ms ease, color 160ms ease, transform 160ms ease;
}
.webinar-control-button:hover { background: #2478ff; transform: translateY(-1px); }
.webinar-progress-slider, .webinar-volume-slider { appearance: none; border: 0; background: transparent; }
.webinar-progress-slider { height: 14px; }
.webinar-progress-slider::-webkit-slider-runnable-track, .webinar-volume-slider::-webkit-slider-runnable-track {
  height: 5px; border-radius: 999px;
  background: linear-gradient(to right, #2d8cff 0%, #2d8cff var(--progress-percent, var(--volume-percent, 0%)), rgb(255 255 255 / 28%) var(--progress-percent, var(--volume-percent, 0%)), rgb(255 255 255 / 28%) 100%);
}
.webinar-volume-slider::-webkit-slider-runnable-track { background: linear-gradient(to right, #2d8cff 0%, #2d8cff var(--volume-percent, 0%), rgb(255 255 255 / 28%) var(--volume-percent, 0%), rgb(255 255 255 / 28%) 100%); }
.webinar-progress-slider::-webkit-slider-thumb, .webinar-volume-slider::-webkit-slider-thumb { appearance: none; width: 13px; height: 13px; margin-top: -4px; border-radius: 999px; background: white; box-shadow: 0 0 0 3px rgb(45 140 255 / 30%); }
.webinar-progress-slider::-moz-range-track, .webinar-volume-slider::-moz-range-track { height: 5px; border-radius: 999px; background: rgb(255 255 255 / 28%); }
.webinar-progress-slider::-moz-range-progress, .webinar-volume-slider::-moz-range-progress { height: 5px; border-radius: 999px; background: #2d8cff; }
.webinar-progress-slider::-moz-range-thumb, .webinar-volume-slider::-moz-range-thumb { width: 13px; height: 13px; border: 0; border-radius: 999px; background: white; }
.webinar-controls-fade-enter-active, .webinar-controls-fade-leave-active { transition: opacity 180ms ease; }
.webinar-controls-fade-enter-from, .webinar-controls-fade-leave-to { opacity: 0; }
</style>
